// @ts-nocheck

// Local debounce function to avoid dependency on global.js
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// Local onKeyUpEscape function to avoid dependency on global.js
function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

// Accordion functionality for facets
function initializeAccordions() {
  const accordionDetails = document.querySelectorAll(
    '.facets__details-vertical.group\\/accordion, .facets__details-vertical.group\\/accordion-price',
  );

  accordionDetails.forEach((details) => {
    const summary = details.querySelector('summary');
    const content = details.querySelector('.details-content');

    if (summary && content) {
      // Ensure proper initial state
      if (details.hasAttribute('open')) {
        content.style.maxHeight = 'none';
      } else {
        content.style.maxHeight = '0';
      }

      // Add click handler for smooth animation
      summary.addEventListener('click', (e) => {
        // Prevent default to handle manually
        e.preventDefault();

        const isOpen = details.hasAttribute('open');

        if (isOpen) {
          // Closing
          details.removeAttribute('open');
          content.style.maxHeight = '0';
          content.classList.remove('animate-accordion-open');
          content.classList.add('animate-accordion-close');
        } else {
          // Opening
          details.setAttribute('open', '');
          content.style.maxHeight = content.scrollHeight + 'px';
          content.classList.remove('animate-accordion-close');
          content.classList.add('animate-accordion-open');
        }

        // Update aria-expanded
        summary.setAttribute('aria-expanded', !isOpen);
      });
    }
  });
}

// Initialize accordions when DOM is ready
document.addEventListener('DOMContentLoaded', initializeAccordions);

// Also initialize after AJAX updates
document.addEventListener('facetFiltersUpdated', initializeAccordions);

// Also update remove all button state after AJAX updates
document.addEventListener('facetFiltersUpdated', () => {
  FacetFiltersForm.toggleRemoveAllButton();
});

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  connectedCallback() {
    // Initialize remove all button state when component is connected
    // Use a small delay to ensure DOM is fully rendered
    setTimeout(() => {
      FacetFiltersForm.toggleRemoveAllButton();
    }, 100);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document
      .getElementById('ProductGridContainer')
      .querySelector('.collection')
      .classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {html, url},
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function')
          initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function')
      initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const productGridContainer = document.getElementById(
      'ProductGridContainer',
    );
    if (productGridContainer) {
      productGridContainer.innerHTML = parsedHTML.getElementById(
        'ProductGridContainer',
      ).innerHTML;

      productGridContainer
        .querySelectorAll('.scroll-trigger')
        .forEach((element) => {
          element.classList.add('scroll-trigger--cancel');
        });

      // Scroll to top of product grid after filter changes
      productGridContainer.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const count = parsedHTML.getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');

    // Update the DOM elements
    container.innerHTML = count;
    container.classList.remove('loading');

    // Hide loading spinners
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    // Dispatch event for count-on-grid component
    const countText = container.textContent.trim();
    document.dispatchEvent(
      new CustomEvent('update:infinite:product-grid', {
        detail: {
          count: countText,
        },
      }),
    );
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (
        !Array.from(facetDetailsElementsFromFetch).some(
          ({id}) => currentElement.id === id,
        )
      ) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
      (element) => !matchesId(element),
    );
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
      matchesId,
    );

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML =
          elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const {className: previousElementClassName, id: previousElementId} =
            facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document
            .querySelector(`#${elementToRender.parentElement.id} .js-filter`)
            .before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    // Dispatch custom event for accordion reinitialization
    document.dispatchEvent(new CustomEvent('facetFiltersUpdated'));

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          event.target.closest('.js-filter'),
        );
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(closestJSFilterID),
        );

        const newFacetDetailsElement =
          document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains(
          'mobile-facets__details',
        )
          ? '.mobile-facets__close-button'
          : '.facets__summary';
        const newElementToActivate =
          newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.active-facets-desktop',
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);

    // Check and toggle remove all button visibility
    FacetFiltersForm.toggleRemoveAllButton();
  }

  static toggleRemoveAllButton() {
    // Find all remove all buttons (both desktop and mobile)
    const removeAllButtons = document.querySelectorAll('.active-facets');

    removeAllButtons.forEach((button) => {
      // Check if there are any active facet pills in the same container
      const container = button.closest('.active-facets, .active-facets-mobile');
      if (!container) return;

      const activeFacetPills = container.querySelectorAll(
        'facet-remove:not(.active-facets__button-wrapper)',
      );
      const hasActiveFilters = activeFacetPills.length > 0;

      // Hide/show the button based on active filters
      if (hasActiveFilters) {
        button.classList.remove('hidden');
        button.removeAttribute('hidden');
        button.classList.add('block');
      } else {
        button.classList.remove('block');
        button.setAttribute('hidden', '');
        button.classList.add('hidden');
      }
    });
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });

    // Re-initialize any custom sort-select elements that may have been added after AJAX updates
    document
      .querySelectorAll('sort-select:not([data-initialized])')
      .forEach((element) => {
        // Mark as initialized to avoid double initialization
        element.setAttribute('data-initialized', 'true');

        // Trigger connectedCallback manually if needed
        if (element.connectedCallback) {
          element.connectedCallback();
        }
      });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) {
      const menuDrawer = mobileForm.closest('menu-drawer');
      if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
        menuDrawer.bindEvents();
      }
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector('show-more-button .label-show-more.hidden'),
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace('hidden', 'show-more-item'),
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      {searchParams},
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById('ProductGridContainer').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const sortFilterForms = document.querySelectorAll(
      'facet-filters-form form',
    );

    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(
        event.target.closest('form'),
      );
      this.onSubmitForm(searchParams, event);
    } else {
      const triggeringFormId = event.target.closest('form').id;
      const isMobile =
        triggeringFormId === 'FacetFiltersFormMobile' ||
        triggeringFormId === 'FacetMobileSortForm';

      // Create a URLSearchParams object to handle merging and avoid duplicates
      const mergedParams = new URLSearchParams();

      sortFilterForms.forEach((form) => {
        let shouldIncludeForm = false;
        let priority = 0;

        if (!isMobile) {
          if (form.id === 'FacetSortForm') {
            shouldIncludeForm = true;
            priority = 2; // Highest priority for desktop sort
          } else if (form.id === 'FacetFiltersForm') {
            shouldIncludeForm = true;
            priority = 1; // Medium priority for filters
          } else if (form.id === 'FacetSortDrawerForm') {
            shouldIncludeForm = true;
            priority = 2; // Highest priority for drawer sort
          }
          // Skip mobile forms when not mobile
        } else {
          // Mobile logic
          if (form.id === 'FacetFiltersFormMobile') {
            shouldIncludeForm = true;
            priority = 1; // Medium priority for mobile filters
          } else if (form.id === 'FacetMobileSortForm') {
            shouldIncludeForm = true;
            priority = 2; // Highest priority for mobile sort
          }
          // Skip desktop forms when mobile
        }

        if (shouldIncludeForm) {
          const formData = new FormData(form);
          for (const [key, value] of formData.entries()) {
            // Special handling for sort_by parameter - prioritize sort forms
            if (key === 'sort_by') {
              if (!mergedParams.has(key) || priority >= 2) {
                mergedParams.set(key, value);
              }
            }
            // Special handling for price parameters - only allow one value
            else if (key.includes('price') || key.includes('Price')) {
              // For price, replace any existing value
              mergedParams.set(key, value);
            }
            // For all other parameters (filters), allow multiple values
            else {
              // Check if this key already exists
              if (mergedParams.has(key)) {
                // Get existing values and add the new one
                const existingValues = mergedParams.getAll(key);
                // Only add if this value doesn't already exist
                if (!existingValues.includes(value)) {
                  mergedParams.append(key, value);
                }
              } else {
                // First value for this key
                mergedParams.set(key, value);
              }
            }
          }
        }
      });

      this.onSubmitForm(mergedParams.toString(), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf('?') + 1,
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// Initialize remove all button state on page load
document.addEventListener('DOMContentLoaded', () => {
  FacetFiltersForm.toggleRemoveAllButton();
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '')
      minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest('facet-filters-form') ||
      document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

class SortSelect extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.selectedOption = null;
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    // Prevent double initialization
    if (this.hasAttribute('data-initialized')) {
      return;
    }
    this.setAttribute('data-initialized', 'true');

    // Check if the structure already exists (from Liquid template)
    const existingTrigger = this.querySelector('.select-trigger');
    const existingDropdown = this.querySelector('.select-dropdown');

    if (!existingTrigger || !existingDropdown) {
      // If structure doesn't exist, create it (fallback)
      this.createStructure();
    }

    // Store initial selection data
    const selectedOption = this.querySelector('sort-option[selected]');
    const initialValue = selectedOption
      ? selectedOption.getAttribute('value')
      : this.getAttribute('value');
    const initialText = selectedOption
      ? selectedOption.textContent.trim()
      : null;

    this.bindEvents();

    // Create hidden input for form submission
    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = this.getAttribute('name');
    this.appendChild(hiddenInput);

    // Set initial selection without triggering events
    if (initialValue) {
      this.setInitialValue(initialValue, initialText);
    }
  }

  createStructure() {
    const placeholder = this.getAttribute('placeholder') || 'Select an option';

    // First, collect all the original options
    const originalOptions = Array.from(this.children).filter(
      (child) => child.tagName.toLowerCase() === 'sort-option',
    );

    // Create the structure
    this.innerHTML = `
                    <div class="select-trigger">
                        <span class="select-text">${placeholder}</span>
                        <span class="select-arrow"><svg width="16" height="9" viewBox="0 0 16 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 0.999999L8 8L15 1" stroke="#313131" stroke-width="1.33333" stroke-miterlimit="10" stroke-linecap="square"/>
                        </svg>
                        </span>
                    </div>
                    <div class="select-dropdown animate-fade-in">
                    </div>
                `;

    // Now add the options to the dropdown
    const dropdown = this.querySelector('.select-dropdown');
    originalOptions.forEach((option) => {
      dropdown.appendChild(option);
    });
  }

  bindEvents() {
    const trigger = this.querySelector('.select-trigger');
    trigger.addEventListener('click', this.toggle.bind(this));

    // Listen for option selection
    this.addEventListener('optionSelected', this.onOptionSelected.bind(this));
    this.querySelector('.select-dropdown').addEventListener(
      'click',
      this.close.bind(this),
    );
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.isOpen = true;
    this.classList.add('open');
    document.addEventListener('click', this.boundHandleClickOutside);
    document.addEventListener('keydown', this.boundHandleKeydown);
  
    // Prevent background scroll on mobile
    if (window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    }
  
    const firstOption = this.querySelector('sort-option');
    if (firstOption) firstOption.focus();
  }
  
  close() {
    this.isOpen = false;
    this.classList.remove('open');
    document.removeEventListener('click', this.boundHandleClickOutside);
    document.removeEventListener('keydown', this.boundHandleKeydown);
  
    // Restore background scroll
    document.body.style.overflow = '';
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  handleKeydown(event) {
    if (!this.isOpen) return;

    switch (event.key) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.navigateOptions(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.navigateOptions(-1);
        break;
      case 'Enter': {
        event.preventDefault();
        const focusedOption = this.querySelector('sort-option:focus');
        if (focusedOption) {
          focusedOption.click();
        }
        break;
      }
    }
  }

  navigateOptions(direction) {
    const options = Array.from(this.querySelectorAll('sort-option'));
    const currentIndex = options.findIndex(
      (option) => option === document.activeElement,
    );
    let newIndex;

    if (currentIndex === -1) {
      newIndex = direction > 0 ? 0 : options.length - 1;
    } else {
      newIndex = currentIndex + direction;
      if (newIndex < 0) newIndex = options.length - 1;
      if (newIndex >= options.length) newIndex = 0;
    }

    options[newIndex].focus();
  }

  onOptionSelected(event) {
    const option = event.detail.option;
    const value = option.getAttribute('value');
    const text = option.textContent.trim();

    // Update selected option
    if (this.selectedOption) {
      this.selectedOption.classList.remove('selected');
    }

    this.selectedOption = option;
    option.classList.add('selected');

    // Update trigger text
    const selectText = this.querySelector('.select-text');
    selectText.textContent = text;

    // Set value attribute
    this.setAttribute('value', value);

    // Update hidden input value for form submission
    const hiddenInput = this.querySelector('input[type="hidden"]');
    if (hiddenInput) {
      hiddenInput.value = value;
    }

    // Close dropdown
    this.close();

    // Dispatch input event specifically for the form
    const inputEvent = new Event('input', {
      bubbles: true,
      cancelable: true,
    });

    // Make sure the event target is the form element
    const form = this.closest('form');
    if (form) {
      form.dispatchEvent(inputEvent);
    } else {
      this.dispatchEvent(inputEvent);
    }

    // Also dispatch change event
    const changeEvent = new Event('change', {
      bubbles: true,
      cancelable: true,
    });

    if (form) {
      form.dispatchEvent(changeEvent);
    } else {
      this.dispatchEvent(changeEvent);
    }
  }

  setValue(value) {
    const option = this.querySelector(`sort-option[value="${value}"]`);
    if (option) {
      option.dispatchEvent(
        new CustomEvent('optionSelected', {
          detail: {option},
          bubbles: true,
        }),
      );
    }
  }

  setInitialValue(value, text) {
    // Set initial value without triggering events
    const option = this.querySelector(`sort-option[value="${value}"]`);
    if (option) {
      // Update selected option
      if (this.selectedOption) {
        this.selectedOption.classList.remove('selected');
      }

      this.selectedOption = option;
      option.classList.add('selected');

      // Update trigger text
      const selectText = this.querySelector('.select-text');
      if (text) {
        selectText.textContent = text;
      } else {
        selectText.textContent = option.textContent.trim();
      }

      // Set value attribute
      this.setAttribute('value', value);

      // Update hidden input value for form submission
      const hiddenInput = this.querySelector('input[type="hidden"]');
      if (hiddenInput) {
        hiddenInput.value = value;
      }
    }
  }

  getValue() {
    return this.getAttribute('value') || '';
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundHandleClickOutside);
    document.removeEventListener('keydown', this.boundHandleKeydown);
  }
}
customElements.define('sort-select', SortSelect);

class SortOption extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.setAttribute('tabindex', '0');
    this.bindEvents();
  }

  bindEvents() {
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  onClick(event) {
    event.stopPropagation();
    this.select();
  }

  onKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.select();
    }
  }

  select() {
    // Dispatch custom event to parent select
    this.dispatchEvent(
      new CustomEvent('optionSelected', {
        detail: {option: this},
        bubbles: true,
      }),
    );
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
  }
}
customElements.define('sort-option', SortOption);

class PriceRangeSlider extends HTMLElement {
  constructor() {
    super();
    this.minRangeValueGap = 6;
    this.initialized = false;
    this.mutationObserver = null;
  }

  connectedCallback() {
    try {
      const mobileDrawer = this.closest('.mobile-facets__wrapper details');

      if (mobileDrawer && !mobileDrawer.hasAttribute('open')) {
        this.waitForDrawerOpen(mobileDrawer);
      } else {
        setTimeout(() => this.initializeElements(), 0);
      }
    } catch (error) {
      console.error('Error in PriceRangeSlider connectedCallback:', error);
    }
  }

  waitForDrawerOpen(drawerElement) {
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'open' &&
          drawerElement.hasAttribute('open') &&
          !this.initialized
        ) {
          setTimeout(() => this.initializeElements(), 100);
        }
      });
    });

    this.mutationObserver.observe(drawerElement, {
      attributes: true,
      attributeFilter: ['open'],
    });

    // Fallback initialization in case drawer is already open
    setTimeout(() => {
      if (drawerElement.hasAttribute('open') && !this.initialized) {
        this.initializeElements();
      }
    }, 500);
  }

  initializeElements() {
    try {
      if (this.initialized) return;

      // Query all required elements
      const elements = {
        minRange: this.querySelector('#min_range_price-filter'),
        maxRange: this.querySelector('#max_range_price-filter'),
        minInput: this.querySelector('#min_input_price-filter'),
        maxInput: this.querySelector('#max_input_price-filter'),
        rangeFill: this.querySelector('#range_fill_price-filter'),
      };

      // Check if all elements are found
      const missingElements = Object.entries(elements)
        .filter(([key, element]) => !element)
        .map(([key]) => key);

      if (missingElements.length > 0) {
        console.error(
          'Price Range Slider elements not found:',
          missingElements,
        );
        return;
      }

      // Assign elements to instance properties
      Object.assign(this, elements);

      this.bindEvents();
      this.initPriceRangeSlider();
      this.initialized = true;

      // Clean up mutation observer
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }
    } catch (error) {
      console.error('Error initializing PriceRangeSlider elements:', error);
    }
  }

  disconnectedCallback() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  bindEvents() {
    try {
      // Range input events
      this.minRange.addEventListener('input', (e) =>
        this.handleRangeChange(e, 'min'),
      );
      this.maxRange.addEventListener('input', (e) =>
        this.handleRangeChange(e, 'max'),
      );

      // Text input events
      this.minInput.addEventListener('input', (e) =>
        this.handleInputChange(e, 'min'),
      );
      this.maxInput.addEventListener('input', (e) =>
        this.handleInputChange(e, 'max'),
      );

      // Focus events for better UX
      const focusEvents = [
        [this.minInput, this.minRange, 'focus', '3'],
        [this.maxInput, this.maxRange, 'focus', '3'],
        [this.minInput, this.minRange, 'blur', '1'],
        [this.maxInput, this.maxRange, 'blur', '1'],
        [this.minRange, this.minRange, 'blur', '1'],
        [this.maxRange, this.maxRange, 'blur', '1'],
      ];

      focusEvents.forEach(([element, target, event, zIndex]) => {
        element.addEventListener(event, () => (target.style.zIndex = zIndex));
      });

      // Range focus events that also focus corresponding inputs
      this.minRange.addEventListener('focus', () => {
        this.minInput.focus();
        this.minRange.style.zIndex = '3';
      });
      this.maxRange.addEventListener('focus', () => {
        this.maxInput.focus();
        this.maxRange.style.zIndex = '3';
      });
    } catch (error) {
      console.error('Error binding events:', error);
    }
  }

  updateRangeFill() {
    try {
      if (!this.minRange || !this.maxRange || !this.rangeFill) {
        throw new Error(
          'Price Range Slider elements not initialized properly.',
        );
      }

      const minValue = parseInt(this.minRange.value);
      const maxValue = parseInt(this.maxRange.value);
      const maxRangeValue = parseInt(this.minRange.max);

      const minPercentage = (minValue / maxRangeValue) * 100;
      const maxPercentage = (maxValue / maxRangeValue) * 100;

      const leftValue = minPercentage + '%';
      const widthValue = maxPercentage - minPercentage + '%';

      // Use CSS custom properties for better control and fallback to inline styles
      this.rangeFill.style.setProperty('--range-fill-left', leftValue);
      this.rangeFill.style.setProperty('--range-fill-width', widthValue);
      this.rangeFill.style.setProperty('left', leftValue, 'important');
      this.rangeFill.style.setProperty('width', widthValue, 'important');
    } catch (error) {
      console.error('Error updating range fill:', error);
    }
  }

  syncInputs() {
    try {
      this.minInput.value = this.minRange.value;
      this.maxInput.value = this.maxRange.value;
    } catch (error) {
      console.error('Error syncing inputs:', error);
    }
  }

  handleRangeChange(event, type) {
    try {
      const minValue = parseInt(this.minRange.value);
      const maxValue = parseInt(this.maxRange.value);

      // Prevent overlap
      if (type === 'min' && minValue >= maxValue - this.minRangeValueGap) {
        this.minRange.value = maxValue - this.minRangeValueGap;
      } else if (
        type === 'max' &&
        maxValue <= minValue + this.minRangeValueGap
      ) {
        this.maxRange.value = minValue + this.minRangeValueGap;
      }

      this.updateRangeFill();
      this.syncInputs();
    } catch (error) {
      console.error('Error handling range change:', error);
    }
  }

  handleInputChange(event, type) {
    try {
      let value = parseInt(event.target.value) || 0;
      const min = parseInt(event.target.dataset.min) || 0;
      const max = parseInt(event.target.dataset.max) || 100;

      // Clamp values
      value = Math.max(min, Math.min(max, value));
      event.target.value = value;

      // Update corresponding range slider
      const rangeId = event.target.dataset.rangeId;
      const rangeSlider = document.getElementById(rangeId);
      if (rangeSlider) {
        rangeSlider.value = value;
      }

      // Prevent overlap
      const minValue = parseInt(this.minRange.value);
      const maxValue = parseInt(this.maxRange.value);

      if (type === 'min' && value >= maxValue - this.minRangeValueGap) {
        this.minRange.value = maxValue - this.minRangeValueGap;
        this.minInput.value = this.minRange.value;
      } else if (type === 'max' && value <= minValue + this.minRangeValueGap) {
        this.maxRange.value = minValue + this.minRangeValueGap;
        this.maxInput.value = this.maxRange.value;
      }

      this.updateRangeFill();
    } catch (error) {
      console.error('Error handling input change:', error);
    }
  }

  initPriceRangeSlider() {
    this.updateRangeFill();
    this.syncInputs();
  }
}
customElements.define('price-range-slider', PriceRangeSlider);

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.mobile-facets__close-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      console.log('closed');
      const wrapper = btn.closest('.mobile-facets__wrapper');
      if (!wrapper) return;
      const summary = wrapper.querySelector('summary');
      if (summary) summary.click();
    });
  });
});
